<template>
    <div class="page-container">
        <v-card
        flat
        class="py-0 px-2 transparent blur top-bar"
        style="pointer-events:all; border:none;"
        elevation="0"
        color="white"
      >
        <v-btn @click="handlePrint(false)" icon class="MoreSpacing">
            <v-icon left color="black">hi-document-magnifying-glass-outline</v-icon>
        </v-btn>
        <v-btn @click="handlePrint(true)" icon class="MoreSpacing">
            <v-icon left color="black">hi-printer-outline</v-icon>
        </v-btn>
        </v-card>
      <div class="scroll-container" ref="scrollContainer" v-html="htmlContent"></div>
    </div>
  </template>
  
  <script>
  import Axios from 'axios';
  import { API_BASE_URL } from '../api/constants';
  import { ALERT_TYPE_ERROR } from '../store/constants';
  
  export default {
    name: 'PrintablePage', 
    mounted() {
      // Retrieve the 'e' parameter from the route query
      if (this.$route.query.e) {
        this.e = this.$route.query.e;
      } else {
        console.warn("Route query parameter 'e' is missing.");
      }
      this.showPage();
    },
    data() {
      return {
        e: "", // Stores the 'e' parameter from the route
        htmlContent: "<h1>Loading Content...</h1>", // Initial placeholder content
      };
    },
    methods: {
      /**
       * Fetches the HTML content from the API based on the 'e' parameter.
       */
      showPage() {
        this.$store.dispatch("showLoading"); 
  
        const baseUrl = API_BASE_URL + "extPage";
        let url = new URL(baseUrl);

        if (this.e) {
          url.searchParams.append('e', this.e);
        } else {
          // If 'e' is critical and not present, you might not want to proceed
          this.$store.dispatch("hideLoading");
          this.htmlContent = "<p>Error: Cannot load content without a valid identifier.</p>";
           this.$store.commit("showAlert", {
              type: ALERT_TYPE_ERROR,
              message: "Cannot load page: Content identifier is missing.",
          });
          return;
        }
  
        Axios.get(url.toString()) // Convert URL object to string for Axios
          .then((response) => {
            this.$store.dispatch("hideLoading"); // Hide loading indicator
            if (response.data && typeof response.data.HtmlPage === 'string') {
              this.htmlContent = response.data.HtmlPage;
            } else {
              // Handle cases where HtmlPage is missing or not a string
              this.htmlContent = "<p>Error: Received invalid content format from the server.</p>";
              this.$store.commit("showAlert", {
                  type: ALERT_TYPE_ERROR,
                  message: "Content received from server was not in the expected format.",
              });
            }
          })
          .catch((error) => {
            this.$store.dispatch("hideLoading"); // Hide loading indicator on error
            let errorMessage = "An unexpected error occurred while fetching page content.";
            if (error.response) {
              // Server responded with a status code outside the 2xx range
              errorMessage = `Server Error ${error.response.status}: ${error.response.data?.message || error.response.data || 'Failed to load content.'}`;
            } else if (error.request) {
              // Request was made but no response was received
              errorMessage = "No response from server. Please check your network connection.";
            } else {
              // Something else happened in setting up the request
              errorMessage = `Request Error: ${error.message}`;
            }
            this.htmlContent = `<p>Error loading content. ${errorMessage}</p>`;
            this.$store.commit("showAlert", { // Show error alert to the user
              type: ALERT_TYPE_ERROR,
              message: errorMessage,
            });
          });
      },
  
      /**
       * Handles the print button click.
       * Opens a new window with the htmlContent, styled for A4 printing.
       */
      handlePrint(print) {
        const printWindowWidth = 800; // Slightly rounded for simplicity
        const printWindowHeight = 1130;
  
        // Attempt to open a new window
        const printWindow = window.open(
          '', // No URL, content will be written dynamically
          '_blank', // Open in a new tab/window
          `width=${printWindowWidth},height=${printWindowHeight},scrollbars=yes,resizable=yes,menubar=no,toolbar=no,location=no,status=no`
        );
  
        if (printWindow) {
          printWindow.document.open();
          printWindow.document.write(`
            <!DOCTYPE html>
            <html lang="en">
            <head>
              <meta charset="UTF-8">
              <meta name="viewport" content="width=device-width, initial-scale=1.0">
              <title>Print Preview - ${document.title}</title>
              <style>
                /* Basic print reset and A4-like styling */
                @page {
                  size: A4; /* Standard A4 size */
                  margin: 20mm 15mm 20mm 15mm; /* Top, Right, Bottom, Left margins */
                }
                body {
                  margin: 0; /* Body margin is controlled by @page now */
                  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif; /* Common system font stack */
                  line-height: 1.6;
                  color: #333;
                }
                /* Ensure images, SVGs, and other replaced elements scale correctly and don't overflow */
                img, svg, video, canvas, embed, object {
                  max-width: 100% !important;
                  height: auto !important;
                  page-break-inside: avoid; /* Try to prevent breaking images across pages */
                }
                /* Hide elements not meant for printing if they have a specific class from htmlContent */
                .no-print-in-popup {
                  display: none !important;
                }
                /* If your htmlContent has its own <style> tags, they should generally apply.
                   However, specificity can be an issue. It's best if critical print styles
                   are defined here or if the htmlContent is designed to be print-friendly. */
                @media print {
                  /* Styles specifically for the print media type */
                  body {
                    -webkit-print-color-adjust: exact; /* Ensures background colors and images print in Chrome/Safari */
                    print-color-adjust: exact; /* Standard property */
                  }
                  /* Hide the print button from the main page if it somehow gets included (shouldn't with this setup) */
                  .top-bar, .print-button {
                      display: none !important;
                  }
                }
              </style>
            </head>
            <body>
              ${this.htmlContent}
            </body>
            </html>
          `);
          printWindow.document.close(); 
          printWindow.onload = () => {
            printWindow.focus(); // Bring the new window to the front
            if (print == true){
            printWindow.print(); // Trigger the browser's print dialog
            }
          }
        } else {
          // Handle cases where the popup window was blocked
          this.$store.commit("showAlert", {
              type: ALERT_TYPE_ERROR,
              message: "Failed to open print window. Please check your browser's pop-up blocker settings for this site.",
          });
        }
      }
    }
  };
  </script>
  
  <style lang="scss" scoped>
  // Scoped styles for this Vue component. These will NOT apply to the print window content.
  .page-container {
    display: flex;
    flex-direction: column;
    height: 100vh; // Full viewport height
    width: 100vw;  // Full viewport width
    overflow: hidden; // Prevent scrollbars on the page container itself
  }
  
    .top-bar {
        display: flex;
        justify-content: center; // Center the buttons horizontally
        align-items: center;     // Center the buttons vertically
        padding: 10px;          // Add some padding around the buttons
    }
  
 .MoreSpacing {
    margin-right: 20px; // Space between buttons
    align-content: center;
  }
  
  .scroll-container {
    flex-grow: 1; // Take up remaining vertical space
    overflow-y: auto; // Enable vertical scrolling for the content
    padding: 25px;    // Add some padding around the injected HTML
    background-color: #ffffff; // Typically white background for content
  }
  
  /* Styling for the content within v-html (htmlContent)
     If htmlContent itself contains <style> tags, those will apply.
     If htmlContent relies on global CSS from your main app, those styles
     will NOT be available in the print window unless explicitly linked or embedded
     in the handlePrint() method's HTML string.
  */
  :deep(.scroll-container) {
    /* You can use :deep() or >>> to style content inside v-html if needed for the main page display,
       but remember these styles won't transfer to the print window automatically.
       Example:
       h1 {
         color: blue;
       }
    */
  }
  </style>
  